import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie, faBriefcase, faTrophy, faCoins,
  faListCheck, faEye, faClockRotateLeft, faCircle,
  faClock, faSpinner, faCircleCheck, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Skeleton } from '../components/common/Skeleton';
import { useCountUp } from '../hooks/useCountUp';
import { useInView } from '../hooks/useInView';
import { useToast } from '../hooks/useToast';
import { analytics } from '../api';
import type { DashboardAnalytics } from '../types';

const STAGE_CONFIG: Record<string, { label: string; color: string }> = {
  discovery: { label: '탐색', color: 'var(--color-info)' },
  qualification: { label: '검증', color: 'var(--color-accent)' },
  proposal: { label: '제안', color: 'var(--color-warning)' },
  negotiation: { label: '협상', color: 'oklch(0.65 0.18 50)' },
  closed_won: { label: '성사', color: 'var(--color-success)' },
  closed_lost: { label: '실패', color: 'var(--color-error)' },
};

const TASK_STATUS_ICON: Record<string, typeof faClock> = { pending: faClock, in_progress: faSpinner, completed: faCircleCheck, delayed: faTriangleExclamation };
const TASK_STATUS_COLOR: Record<string, string> = { pending: 'var(--color-text-tertiary)', in_progress: 'var(--color-info)', completed: 'var(--color-success)', delayed: 'var(--color-error)' };

function timeAgo(d: string) {
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60000) return '방금'; if (ms < 3600000) return `${Math.floor(ms / 60000)}분 전`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}시간 전`; return `${Math.floor(ms / 86400000)}일 전`;
}

function StatCard({ icon, label, value, suffix = '', color }: { icon: typeof faBriefcase; label: string; value: number; suffix?: string; color: string }) {
  const { ref, isInView } = useInView(0.2);
  const count = useCountUp(value, 1500, 0, isInView);
  return (
    <div ref={ref}>
      <GlassCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div style={{
            width: '3rem', height: '3rem', borderRadius: 'var(--radius-lg)',
            background: `color-mix(in oklch, ${color} 12%, transparent)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: 'var(--text-xl)' }} />
          </div>
          <div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>{label}</div>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 800, fontFamily: 'var(--font-display)' }}>{count}{suffix}</div>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}

export function Analytics() {
  const [data, setData] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    analytics.dashboard().then(setData).catch(() => addToast('error', '분석 데이터를 불러올 수 없습니다')).finally(() => setLoading(false));
  }, [addToast]);

  if (loading) return (
    <main id="main-content" className="container" style={{ paddingTop: 'var(--space-20)' }}>
      <Skeleton height="2rem" width="200px" style={{ marginBottom: 'var(--space-8)' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height="100px" borderRadius="var(--radius-xl)" />)}
      </div>
    </main>
  );

  const d = data!;
  const pipelineTotal = d.deals_by_stage.filter(s => s.stage !== 'closed_lost').reduce((sum, s) => sum + s.total_value, 0);
  const winRate = d.total_deals > 0 ? Math.round((d.won_deals / d.total_deals) * 100) : 0;

  return (
    <main id="main-content" role="main" className="container" style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-16)' }}>
      <FadeUp>
        <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>
          <FontAwesomeIcon icon={faChartPie} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-3)' }} />
          분석
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-8)' }}>딜 파이프라인 한눈에 보기</p>
      </FadeUp>

      {/* KPI Cards */}
      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}>
        <FadeUp delay={0}><StatCard icon={faBriefcase} label="전체 딜" value={d.total_deals} color="var(--color-accent)" /></FadeUp>
        <FadeUp delay={0.05}><StatCard icon={faTrophy} label="성사율" value={winRate} suffix="%" color="var(--color-success)" /></FadeUp>
        <FadeUp delay={0.1}><StatCard icon={faCoins} label="파이프라인 가치" value={Math.round(pipelineTotal / 10000)} suffix="M" color="var(--color-warning)" /></FadeUp>
        <FadeUp delay={0.15}><StatCard icon={faEye} label="문서 열람" value={d.document_views.count} color="var(--color-info)" /></FadeUp>
      </div>

      {/* Pipeline Funnel */}
      <FadeUp delay={0.2}>
        <GlassCard style={{ marginBottom: 'var(--space-8)' }}>
          <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-6)', fontFamily: 'var(--font-display)' }}>
            <FontAwesomeIcon icon={faChartPie} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-2)' }} />
            딜 파이프라인
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {Object.entries(STAGE_CONFIG).map(([stage, cfg]) => {
              const stageData = d.deals_by_stage.find(s => s.stage === stage);
              const count = stageData?.count || 0;
              const maxCount = Math.max(...d.deals_by_stage.map(s => s.count), 1);
              return (
                <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{ width: '100px', fontSize: 'var(--text-sm)', fontWeight: 500, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <FontAwesomeIcon icon={faCircle} style={{ fontSize: '6px', color: cfg.color }} />
                    {cfg.label}
                  </div>
                  <div style={{ flex: 1, height: '24px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      height: '100%', width: `${(count / maxCount) * 100}%`, minWidth: count > 0 ? '4px' : 0,
                      background: cfg.color, borderRadius: 'var(--radius-sm)', transition: 'width 1s var(--ease-out-expo)',
                    }} />
                  </div>
                  <div style={{ width: '80px', textAlign: 'right', fontSize: 'var(--text-sm)', fontWeight: 600, flexShrink: 0 }}>
                    {count} <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>딜</span>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      </FadeUp>

      <div className="analytics-bottom" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)' }}>
        {/* Task Stats */}
        <FadeUp delay={0.25}>
          <GlassCard>
            <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-4)', fontFamily: 'var(--font-display)' }}>
              <FontAwesomeIcon icon={faListCheck} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-2)' }} />
              태스크 현황
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-3)' }}>
              {d.task_stats.map(ts => (
                <div key={ts.status} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                  <FontAwesomeIcon icon={TASK_STATUS_ICON[ts.status] || faClock} style={{ color: TASK_STATUS_COLOR[ts.status] || 'var(--color-text-tertiary)' }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)' }}>{ts.count}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>{ts.status.replace('_', ' ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </FadeUp>

        {/* Recent Activity */}
        <FadeUp delay={0.3}>
          <GlassCard>
            <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-4)', fontFamily: 'var(--font-display)' }}>
              <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-2)' }} />
              최근 활동
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {d.recent_activity.slice(0, 10).map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', fontSize: 'var(--text-sm)', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: 'var(--radius-full)', background: 'var(--color-accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: 'var(--color-accent)', flexShrink: 0 }}>
                    {(a.user_name || '?')[0].toUpperCase()}
                  </div>
                  <span style={{ flex: 1 }}><strong>{a.user_name}</strong> {a.action} a {a.entity_type}</span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{timeAgo(a.created_at)}</span>
                </div>
              ))}
              {d.recent_activity.length === 0 && <p style={{ color: 'var(--color-text-tertiary)', textAlign: 'center', padding: 'var(--space-6)' }}>최근 활동이 없습니다</p>}
            </div>
          </GlassCard>
        </FadeUp>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .kpi-grid { grid-template-columns: repeat(4, 1fr) !important; }
          .analytics-bottom { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </main>
  );
}
